/**
 * L'heure du monde, telle que le client la SUIT.
 *
 * POURQUOI CE MODULE EXISTE. L'heure du monde appartient au serveur depuis
 * l'etape deux du multijoueur, mais le client continue de la faire couler entre
 * deux messages — et cette regle-la, faite de trois constantes et de deux
 * bascules, tenait dans la boucle de jeu au milieu du rendu et de la saisie.
 * Elle n'a pourtant besoin de rien d'autre que du temps qui passe. Sortie ici,
 * elle se rejoue sans navigateur, comme `client/render/resolution.ts`.
 *
 * Le TEMPS DE JEU lui-meme reste dans `Jeu`: vingt-cinq endroits le lisent. Ce
 * module ne rend que les PAS a lui ajouter, ce qui garde l'arithmetique de la
 * boucle mot pour mot celle d'avant.
 */
import { clamp } from '../shared/sim/alea.ts'
import { journal } from './log/journal.ts'

/**
 * Ecart au-dela duquel on ne rattrape plus l'heure du serveur en douceur, en
 * millisecondes de temps de jeu (une journee en vaut 1 440 000).
 *
 * Cinq secondes, c'est le hoquet d'un onglet passe en arriere-plan: on
 * l'absorbe sans que le ciel bouge. Au-dela, ce n'est plus une derive mais un
 * EVENEMENT — quelqu'un a dormi, le monde a ete remis a zero — et il faut
 * sauter: le rattraper au ralenti laisserait le joueur une minute entiere a
 * une heure qui n'est celle de personne.
 */
export const SAUT_HORLOGE_MS = 5000

/**
 * Part du temps ecoule consacree au rattrapage, dans un sens comme dans
 * l'autre.
 *
 * En retard, l'horloge locale avance de 125 % ; en avance, de 75 % — mais
 * JAMAIS a l'envers. Une horloge qui recule ferait repousser un arbre deja
 * abattu et rendrait mur un jardin qu'on vient de cueillir.
 */
export const RATTRAPAGE_HORLOGE = 0.25

export class HorlogeSuivie {
  /** Ecart restant a rattraper entre l'heure locale et celle du serveur. */
  private ecart = 0

  /**
   * Vrai des que le serveur a donne l'heure une premiere fois.
   *
   * Elle commande une chose qui n'a l'air de rien: l'horloge du monde partage
   * avance au TEMPS REEL, pas au rythme des images. Le `dt` d'une image est
   * borne a 100 ms pour que le bonhomme ne traverse pas la carte quand l'onglet
   * revient au premier plan; s'en servir pour l'heure faisait tourner l'horloge
   * a un dixieme de sa vitesse sur une machine qui rend une image par seconde
   * — releve: presque deux minutes de jeu d'ecart entre trois navigateurs. Un
   * retard pareil ne se rattrape pas a 25 %: il se creuse.
   *
   * Le joueur SEUL, lui, garde exactement l'horloge qu'il a toujours eue.
   */
  private suitServeur = false

  /**
   * Se cale sur l'heure du serveur.
   *
   * L'heure du monde n'appartient plus au client: il l'affiche. Mais il continue
   * de la faire avancer entre deux messages, sinon l'horloge sauterait de deux
   * secondes en deux secondes au lieu de couler.
   *
   * Renvoie l'heure a poser d'un coup, ou `null` quand il n'y a qu'a deriver.
   */
  recaler(tempsJeu: number, courant: number, dUnCoup: boolean): number | null {
    this.suitServeur = true
    const ecart = tempsJeu - courant
    if (dUnCoup || Math.abs(ecart) > SAUT_HORLOGE_MS) {
      if (Math.abs(ecart) > 1000) {
        journal.info('temps', `horloge recalee de ${Math.round(ecart / 1000)} s sur le serveur`)
      }
      this.ecart = 0
      return tempsJeu
    }
    this.ecart = ecart
    return null
  }

  /**
   * Ce que le temps de jeu gagne naturellement pendant cette image, en ms.
   *
   * `brut` est le temps reel ecoule, `dt` le meme borne a 100 ms. On suit le
   * temps REEL des que l'heure vient du serveur; seule reste bornee la part
   * qu'une image rattrape, pour qu'un onglet revenu d'une heure d'absence ne
   * balaie pas la nuit en une frame — le serveur, lui, la remettra a l'heure
   * d'un coup.
   */
  pasNaturel(dt: number, brut: number): number {
    return (this.suitServeur ? clamp(brut, 0, 1) : dt) * 1000
  }

  /** Ce que le rattrapage ajoute (ou retire) pendant cette image, en ms. */
  pasRattrapage(dt: number): number {
    if (this.ecart === 0) return 0
    const pas = Math.min(Math.abs(this.ecart), dt * 1000 * RATTRAPAGE_HORLOGE)
    const signe = this.ecart > 0 ? 1 : -1
    this.ecart -= signe * pas
    return signe * pas
  }

  /** Dormir deplace l'heure d'un coup: il n'y a plus rien a rattraper. */
  poseeDUnCoup(): void {
    this.ecart = 0
  }

  /**
   * Retour au regime solo. Sans serveur pour la recaler, l'horloge doit
   * reprendre le rythme des images, celui qu'elle a toujours eu hors ligne.
   */
  repasserEnSolo(): void {
    this.suitServeur = false
    this.ecart = 0
  }
}
